import { useState } from 'react';
import {
  Plus,
  Search,
  GitBranch,
  Mail,
  Clock,
  CheckSquare,
  GitCommitHorizontal,
  X,
  UserPlus,
  UserMinus,
} from 'lucide-react';
import {
  useSequences,
  useSequence,
  useCreateSequence,
  useEnrollContact,
  useUnenrollContact,
  useEnrollments,
  type SequenceFilters,
  type Sequence,
  type SequenceStatus,
  type SequenceStepType,
  type Enrollment,
} from '../hooks/useSequences';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface SequencesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<SequenceStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  active: { label: 'Active', color: 'green' },
  paused: { label: 'Paused', color: 'orange' },
  archived: { label: 'Archived', color: 'slate' },
};

const stepTypeIcons: Record<SequenceStepType, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  wait: Clock,
  task: CheckSquare,
  condition: GitCommitHorizontal,
};

const stepTypeLabels: Record<SequenceStepType, string> = {
  email: 'Send Email',
  wait: 'Wait',
  task: 'Create Task',
  condition: 'Condition',
};

const enrollmentStatusLabels: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'green' },
  completed: { label: 'Completed', color: 'blue' },
  unenrolled: { label: 'Unenrolled', color: 'gray' },
  failed: { label: 'Failed', color: 'red' },
};

export function SequencesView({ onNavigate }: SequencesViewProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [enrollContactId, setEnrollContactId] = useState('');
  const [formData, setFormData] = useState({ name: '', description: '' });

  const filters: SequenceFilters = {
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useSequences(filters);
  const { data: selectedSequence } = useSequence(selectedSequenceId);
  const { data: enrollmentsData } = useEnrollments(selectedSequenceId);
  const createSequence = useCreateSequence();
  const enrollContact = useEnrollContact();
  const unenrollContact = useUnenrollContact();

  const sequences = data?.sequences ?? [];
  const enrollments = enrollmentsData?.enrollments ?? [];

  function handleCreate() {
    createSequence.mutate(
      { name: formData.name, description: formData.description || undefined },
      {
        onSuccess: () => {
          setShowForm(false);
          setFormData({ name: '', description: '' });
        },
      },
    );
  }

  function handleEnroll() {
    if (!selectedSequenceId || !enrollContactId) return;
    enrollContact.mutate(
      { sequenceId: selectedSequenceId, contactId: enrollContactId },
      {
        onSuccess: () => {
          setShowEnrollForm(false);
          setEnrollContactId('');
        },
      },
    );
  }

  function handleUnenroll(enrollment: Enrollment) {
    if (!selectedSequenceId) return;
    unenrollContact.mutate({ sequenceId: selectedSequenceId, enrollmentId: enrollment.id });
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Sequences</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} sequence${data.total !== 1 ? 's' : ''}` : 'Drip sequences'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          New Sequence
        </button>
      </div>

      {/* Search */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <form onSubmit={(e) => e.preventDefault()} className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search sequences..."
            className="input pl-9"
          />
        </form>
      </div>

      <div className="flex">
        {/* List */}
        <div className={cn('flex-1 p-6', selectedSequenceId && 'pr-0')}>
          {error ? (
            <div className="card p-8 text-center text-sm text-red-600">Failed to load sequences.</div>
          ) : isLoading ? (
            <div className="card">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="flex-1" />
                  <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
                </div>
              ))}
            </div>
          ) : sequences.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-200 bg-surface-50">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Name</th>
                    <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Steps</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Enrolled</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Completion Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {sequences.map((seq) => {
                    const sCfg = statusLabels[seq.status];
                    return (
                      <tr
                        key={seq.id}
                        className={cn('hover:bg-surface-50 cursor-pointer', selectedSequenceId === seq.id && 'bg-brand-50')}
                        onClick={() => setSelectedSequenceId(seq.id)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{seq.name}</p>
                          {seq.description && <p className="text-xs text-slate-400 truncate max-w-xs">{seq.description}</p>}
                        </td>
                        <td className="px-4 py-3 text-center"><Badge color={sCfg.color} size="sm">{sCfg.label}</Badge></td>
                        <td className="px-4 py-3 text-right text-sm text-slate-600">{seq.steps.length}</td>
                        <td className="px-4 py-3 text-right text-sm text-slate-600">{seq.enrollmentCount}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-2 w-16 rounded-full bg-surface-200">
                              <div className="h-2 rounded-full bg-green-500" style={{ width: `${Math.min(seq.completionRate, 100)}%` }} />
                            </div>
                            <span className="text-xs text-slate-600">{Math.round(seq.completionRate)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={GitBranch}
              title="No sequences yet"
              description="Create a sequence to automate outreach."
              actionLabel="New Sequence"
              onAction={() => setShowForm(true)}
            />
          )}
        </div>

        {/* Detail Panel */}
        {selectedSequenceId && selectedSequence && (
          <div className="w-[420px] border-l border-surface-200 bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{selectedSequence.name}</h2>
              <button onClick={() => setSelectedSequenceId(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Steps */}
            <div className="mb-6">
              <p className="text-xs font-medium text-slate-500 mb-3">Steps ({selectedSequence.steps.length})</p>
              <div className="space-y-2">
                {selectedSequence.steps.map((step) => {
                  const StepIcon = stepTypeIcons[step.type] ?? GitCommitHorizontal;
                  return (
                    <div key={step.id} className="flex items-center gap-3 rounded-lg border border-surface-200 px-3 py-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded bg-surface-100">
                        <StepIcon className="h-3.5 w-3.5 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{step.name || stepTypeLabels[step.type]}</p>
                        {step.type === 'wait' && step.delayDays !== undefined && (
                          <p className="text-xs text-slate-500">Wait {step.delayDays}d {step.delayHours ? `${step.delayHours}h` : ''}</p>
                        )}
                      </div>
                      <span className="text-2xs text-slate-400">#{step.order}</span>
                    </div>
                  );
                })}
                {selectedSequence.steps.length === 0 && (
                  <p className="text-sm text-slate-500">No steps configured.</p>
                )}
              </div>
            </div>

            {/* Enrollments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-slate-500">Enrollments ({enrollments.length})</p>
                <button className="btn-secondary btn-sm" onClick={() => setShowEnrollForm(true)}>
                  <UserPlus className="h-3 w-3" /> Enroll
                </button>
              </div>
              {enrollments.length > 0 ? (
                <div className="space-y-2">
                  {enrollments.map((enrollment) => {
                    const eCfg = enrollmentStatusLabels[enrollment.status] ?? enrollmentStatusLabels.active;
                    return (
                      <div key={enrollment.id} className="flex items-center justify-between rounded-lg border border-surface-200 px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{enrollment.contactName ?? enrollment.contactId}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge color={eCfg.color} size="sm">{eCfg.label}</Badge>
                            <span className="text-2xs text-slate-400">Step {enrollment.currentStepIndex + 1}</span>
                          </div>
                        </div>
                        {enrollment.status === 'active' && (
                          <button
                            className="btn-secondary btn-sm text-red-600"
                            onClick={() => handleUnenroll(enrollment)}
                            disabled={unenrollContact.isPending}
                            title="Unenroll"
                          >
                            <UserMinus className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No enrollments yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Sequence Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Sequence</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label mb-1">Name</label>
                <input className="input" value={formData.name} onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="label mb-1">Description</label>
                <textarea className="input" rows={3} value={formData.description} onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createSequence.isPending || !formData.name}>
                {createSequence.isPending ? 'Creating...' : 'Create Sequence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enroll Contact Modal */}
      {showEnrollForm && selectedSequenceId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Enroll Contact</h2>
              <button onClick={() => setShowEnrollForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div>
              <label className="label mb-1">Contact ID</label>
              <input className="input" value={enrollContactId} onChange={(e) => setEnrollContactId(e.target.value)} placeholder="Enter contact ID" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowEnrollForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleEnroll} disabled={enrollContact.isPending || !enrollContactId}>
                {enrollContact.isPending ? 'Enrolling...' : 'Enroll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
